/**
 * Exportación completa a CSV y JSON (§9: "los datos son del usuario").
 *
 * Se exporta el valor tal como está guardado, con su unidad al lado, sin convertir ni
 * redondear: un export que "arregla" los datos deja de ser el respaldo de lo que pasó.
 */

import type {
  DiaBloqueado,
  Ejercicio,
  IngestaImagen,
  Maquina,
  MedidaCorporal,
  Plan,
  RegistroCardio,
  Rutina,
  RutinaDia,
  RutinaEjercicio,
  Serie,
  Sesion,
  Usuario,
} from './types.ts';

export interface DatosExportables {
  usuario: Usuario;
  ejercicios: Ejercicio[];
  maquinas: Maquina[];
  planes: Plan[];
  diasBloqueados: DiaBloqueado[];
  rutinas: Rutina[];
  rutinaDias: RutinaDia[];
  rutinaEjercicios: RutinaEjercicio[];
  sesiones: Sesion[];
  series: Serie[];
  cardio: RegistroCardio[];
  medidas: MedidaCorporal[];
  ingestas: IngestaImagen[];
}

export interface ArchivoExportado {
  nombre: string;
  contenido: string;
  mime: string;
}

/** Escapa un valor para CSV: comillas dobles duplicadas y campo entrecomillado si hace falta. */
export function celdaCSV(valor: unknown): string {
  if (valor === null || valor === undefined) return '';
  if (Array.isArray(valor)) return celdaCSV(valor.join('|'));
  if (typeof valor === 'object') return celdaCSV(JSON.stringify(valor));

  const s = String(valor);
  // El punto y coma y el salto de línea rompen el CSV; la coma decimal no se usa acá a
  // propósito, para que el archivo se pueda parsear en cualquier locale.
  return /[",\n\r]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

export function aCSV(filas: Record<string, unknown>[], columnas?: string[]): string {
  const cols = columnas ?? [...new Set(filas.flatMap((f) => Object.keys(f)))];
  const cabecera = cols.map(celdaCSV).join(',');
  const cuerpo = filas.map((f) => cols.map((c) => celdaCSV(f[c])).join(','));
  return [cabecera, ...cuerpo].join('\n');
}

const TABLAS: (keyof DatosExportables)[] = [
  'ejercicios',
  'maquinas',
  'planes',
  'diasBloqueados',
  'rutinas',
  'rutinaDias',
  'rutinaEjercicios',
  'sesiones',
  'series',
  'cardio',
  'medidas',
  'ingestas',
];

export function exportarCSV(datos: DatosExportables): ArchivoExportado[] {
  const archivos: ArchivoExportado[] = [
    {
      nombre: 'usuario.csv',
      contenido: aCSV([datos.usuario as unknown as Record<string, unknown>]),
      mime: 'text/csv',
    },
  ];

  for (const tabla of TABLAS) {
    const filas = datos[tabla] as unknown as Record<string, unknown>[];
    archivos.push({
      nombre: `${tabla}.csv`,
      contenido: aCSV(Array.isArray(filas) ? filas : []),
      mime: 'text/csv',
    });
  }
  return archivos;
}

export function exportarJSON(datos: DatosExportables): ArchivoExportado {
  return {
    nombre: 'mg-export.json',
    contenido: JSON.stringify({ version: 1, exportado_en: null, ...datos }, null, 2),
    mime: 'application/json',
  };
}

/**
 * `exportadoEn` se pasa desde afuera en vez de llamar a `new Date()` acá: mantiene esta
 * función pura y hace que el export sea reproducible en los tests.
 */
export function exportarTodo(datos: DatosExportables, exportadoEn: string): ArchivoExportado[] {
  const json = exportarJSON(datos);
  const conFecha: ArchivoExportado = {
    ...json,
    contenido: JSON.stringify({ version: 1, exportado_en: exportadoEn, ...datos }, null, 2),
  };
  return [...exportarCSV(datos), conFecha];
}
